import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Menu,
  ListItemText,
  Chip,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { MoreVert } from "@mui/icons-material";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { LockedIcon, UnlockedIcon } from "../Common/assets/Icons";
import { fetchAdministratorsList } from "../../Redux/actions/usersAction";
import {
  refreshAdministratorsPage,
  setAdministratorsPage,
} from "../../Redux/reducers/administratorsSlice";
import NewUserModal from "./Modals/NewUserModal";
import StatusBadge from "../Common/UI/StatusTag";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../utils/dataTableShell";

const statusMap: any = {
  invited: { label: "Invited", variant: "pending" },
  accepted: { label: "Accepted", variant: "active" },
};
type Props = {
  canEdit: boolean;
};

const UsersTable = ({ canEdit }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    loading,
    administratorsData,
    totalAdministrators,
    paginationModel,
    // type,
    searchText,
    sortOrder,
    sortColumn,
    toggleLoader,
  } = useAppSelector((state) => state.administrators);

  const [selectedRow, setSelectedRow] = useState<any>({});
  const [showModal, setShowModal] = useState(false);

  const [menuLoader, setMenuLoader] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const openModal = (row: any) => {
    setSelectedRow(row);
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  useEffect(() => {
    dispatch(
      fetchAdministratorsList(
        paginationModel.page,
        sortColumn,
        sortOrder,
        searchText,
        id
      )
    );
  }, [
    dispatch,
    paginationModel,
    sortOrder,
    sortColumn,
    searchText,
    toggleLoader,
    id,
  ]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const modifyUser = useCallback(
    async (row: any, action: string) => {
      let body: any = {};
      let url: string = ``;
      try {
        setMenuLoader(true);
        if (action === "lock-unlock") {
          const userId = localStorage.getItem("user-id");
          if (row?.userId === userId) {
            toastMessage("error", "Locking your own account is not allowed.");
            setMenuLoader(false);
            return;
          }
          url = `/study/${id}/users/${row?.userId}/access-status`;
          body = {
            studyAccessStatus:
              row?.studyAccessStatus === "locked" ? "active" : "locked",
          };
        } else if (action === "re-invite") {
          url = `/study/${id}/users/reinvite`;
          body = {
            userId: row?.userId,
          };
        }
        const res: AxiosResponse = await http.post(url, body);
        handleMenuClose();
        setMenuLoader(false);
        dispatch(refreshAdministratorsPage());
        toastMessage("success", res.data.message);
      } catch (err) {
        setMenuLoader(false);
        errorToastMessage(err as Error);
      }
    },
    [dispatch, id]
  );

  const handleCellClick = (row: any) => {
    navigate(`/studies/${id}/users/${row?.userId}`);
  };

  const handlePageChange = (_1: any, newPage: number) => {
    dispatch(setAdministratorsPage({ page: newPage, pageSize: pageSize }));
  };

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        footer={
          <DataTableFooter
            filteredCount={totalAdministrators}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            onPageChange={handlePageChange}
            loading={loading}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ minWidth: 120 }}>
                First Name
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: 120 }}>
                Last Name
              </StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Study Role</StyledTableCell>
              <StyledTableCell>Role(s)</StyledTableCell>
              <StyledTableCell>Site(s)</StyledTableCell>
              <StyledTableCell>Email Address</StyledTableCell>
              <StyledTableCell align="center">
                <LockedIcon />
              </StyledTableCell>
              {canEdit && <StyledTableCell />}
            </TableRow>
          </TableHead>
          {!loading && totalAdministrators > 0 && (
            <>
              <TableBody>
                {administratorsData?.map((row: any) => (
                  <TableRow key={row?.id}>
                    <StyledTableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleCellClick(row)}
                    >
                      {row?.firstName}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleCellClick(row)}
                    >
                      {row?.lastName}
                    </StyledTableCell>
                    <StyledTableCell sx={{ textTransform: "capitalize" }}>
                      <StatusBadge
                        variant={statusMap[row?.status]?.variant}
                        label={statusMap[row?.status]?.label ?? row?.status}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      {row?.studyRole?.role?.label || "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row?.roleLabels?.length + 1}
                    </StyledTableCell>
                    <StyledTableCell sx={{ minWidth: 130 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        {row?.visibleSites?.map(
                          (site: any) =>
                            site?.id && (
                              <Chip
                                key={site?.id}
                                label={site?.name}
                                sx={{
                                  borderRadius: 1,
                                  maxWidth: "15ch",
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  fontWeight: 500,
                                  fontsize: 14,
                                }}
                              />
                            )
                        )}{" "}
                        {row?.hiddenSites?.length > 0 && (
                          <Typography>+{row?.hiddenSites?.length}</Typography>
                        )}
                      </Box>
                    </StyledTableCell>

                    <StyledTableCell>{row?.email}</StyledTableCell>

                    <StyledTableCell sx={{ minWidth: 50 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {row?.studyAccessStatus === "locked" ? (
                          <LockedIcon />
                        ) : (
                          <UnlockedIcon />
                        )}
                      </Box>
                    </StyledTableCell>

                    {canEdit && (
                      <StyledTableCell align="right">
                        <IconButton onClick={(e) => handleMenuClick(e, row)}>
                          <MoreVert />
                        </IconButton>
                      </StyledTableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
        {!loading && totalAdministrators === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          TransitionProps={{
            exit: false,
          }}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              openModal(selectedRow);
            }}
          >
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              const action =
                selectedRow?.status === "accepted"
                  ? "lock-unlock"
                  : "re-invite";
              modifyUser(selectedRow, action);
            }}
            disabled={menuLoader}
          >
            <ListItemText>
              {selectedRow?.status === "accepted"
                ? selectedRow?.studyAccessStatus === "locked"
                  ? "Unlock"
                  : "Lock"
                : "Reinvite"}
            </ListItemText>
            {menuLoader && (
              <ListItemIcon>
                <CircularProgress size={18} sx={{ ml: 1 }} />
              </ListItemIcon>
            )}
          </MenuItem>
        </Menu>

        {showModal && (
          <NewUserModal
            showModal={showModal}
            closeModal={closeModal}
            data={selectedRow}
          />
        )}
    </Box>
  );
};

export default UsersTable;
